import {
  percentuaisVariaveis,
  type Cenario,
  type Parametros,
  type Recomendacao,
} from '@favo/core';
import type * as React from 'react';

import type { TomSelo } from '@/componentes/selo-estado';
import { Valor } from '@/componentes/valor';
import { formatarMoeda, formatarPercentual, formatarPercentualPontos } from '@/design/formato';

/**
 * A ÚNICA tradução de `recomendacao.estado` em interface. Selo, capa e
 * recomendações leem daqui, então é impossível o topo dizer uma coisa e o fim
 * do documento dizer outra.
 *
 * Módulo separado de propósito: enquanto ele for o único a ler `rec.estado`
 * para produzir tom e cifra, não existe um segundo julgamento no laudo.
 */
export type Veredito = {
  tom: TomSelo;
  selo: string;
  /** De que grandeza fala a cifra da capa. */
  rotulo: string;
  cifra: React.ReactNode;
  /** Comparação de → para. Só existe onde há um segundo preço legítimo. */
  de?: string;
  para?: string;
  variacao?: string;
  explicacao: React.ReactNode;
};

export function veredito(p: Parametros, rec: Recomendacao, atual: Cenario | null): Veredito {
  // A meta é a régua de TODO veredito deste documento, e quando ela não foi
  // definida o motor usa 12% (parametros.ts). Sem a ressalva ao lado do número,
  // o laudo compara a empresa com uma meta que ela nunca escolheu e apresenta
  // isso como decisão dela. A ressalva vivia só no anexo, em corpo menor.
  const alvo = p.lucroAlvoDefinido
    ? formatarPercentualPontos(p.lucroAlvo)
    : `${formatarPercentualPontos(p.lucroAlvo)} (padrão do sistema)`;

  switch (rec.estado) {
    case 'abaixo':
      return {
        tom: 'atencao',
        selo: 'Preço abaixo do mínimo para a meta',
        rotulo: `Falta no preço para a meta de ${alvo}`,
        cifra: <Valor valor={rec.reajuste} formato="moeda" tamanho="display" />,
        de: formatarMoeda(p.ticket),
        para: formatarMoeda(rec.precoSugerido),
        variacao: `+${formatarPercentual(rec.reajustePct)}`,
        explicacao: (
          <>
            O preço praticado hoje não cobre custo, despesa variável e rateio da despesa fixa com{' '}
            <b>{alvo}</b> de lucro líquido sobrando. O mínimo que fecha essa conta é{' '}
            <b>{formatarMoeda(rec.precoSugerido)}</b>, e ele é piso, não teto.
          </>
        ),
      };

    case 'no-ponto':
      return {
        tom: 'positivo',
        selo: 'Preço no ponto',
        rotulo: 'Preço praticado',
        cifra: <Valor valor={p.ticket} formato="moeda" tamanho="display" />,
        explicacao: (
          <>
            O preço está dentro da tolerância de <b>{formatarMoeda(rec.tolerancia)}</b> em torno do
            mínimo para a meta de <b>{alvo}</b> de lucro líquido. Não há ajuste de preço a fazer
            agora.
          </>
        ),
      };

    case 'acima':
      // REGRA DE OURO: só o preço atual aparece. O mínimo existe no objeto e
      // continua fora da página de propósito.
      return {
        tom: 'positivo',
        selo: 'Margem acima da meta',
        rotulo: 'Preço praticado',
        cifra: <Valor valor={p.ticket} formato="moeda" tamanho="display" />,
        explicacao: (
          <>
            A margem líquida atual, de <b>{formatarPercentual(atual?.lucroPct ?? null)}</b>, supera
            a meta de <b>{alvo}</b>. Sobra <b>{formatarMoeda(rec.folga)}</b> por venda,{' '}
            <b>{formatarPercentual(rec.folgaPct)}</b> do preço. Essa folga é poder de negociação:
            dá para fechar contrato estratégico ou absorver custo pontual sem furar a meta. Não é
            motivo para baixar o preço de tabela.
          </>
        ),
      };

    case 'meta-inviavel': {
      const pcts = percentuaisVariaveis(p);
      return {
        tom: 'negativo',
        selo: 'Meta inviável com os percentuais atuais',
        rotulo: 'Percentuais sobre o preço',
        cifra: <Valor valor={pcts.total + pcts.serv} formato="percentual" tamanho="display" />,
        explicacao: (
          <>
            Comissão, tributos e demais despesas percentuais somam isso de cada venda. Com a meta
            de <b>{alvo}</b> de lucro líquido, o total compromete 100% ou mais do preço: nenhum
            preço atinge essa meta, porque cada real a mais de preço traz junto mais comissão, mais
            tributo e mais meta. A saída é baixar percentual em Despesas ou baixar a meta em
            Empresa.
          </>
        ),
      };
    }

    case 'prejuizo': {
      const perda = rec.atual.lucroUnit === null ? null : Math.abs(rec.atual.lucroUnit);
      return {
        tom: 'negativo',
        selo: 'Prejuízo no preço praticado',
        rotulo: `Preço mínimo para a meta de ${alvo}`,
        cifra: <Valor valor={rec.precoSugerido} formato="moeda" tamanho="display" />,
        explicacao: (
          <>
            Cada projeto vendido a <b>{formatarMoeda(p.ticket)}</b>
            {perda === null ? ' não se paga' : <> perde <b>{formatarMoeda(perda)}</b></>}, depois de
            comissão, tributo e do rateio da despesa fixa. Este não é um preço abaixo da meta, é um
            preço abaixo do custo. O projeto passa a se pagar a partir de{' '}
            <b>{formatarMoeda(rec.precoLucroZero)}</b>, e entrega a meta de <b>{alvo}</b> a partir
            de <b>{formatarMoeda(rec.precoSugerido)}</b>.
          </>
        ),
      };
    }

    case 'percentual-invalido': {
      const lista = rec.invalidos
        .map((i) => `${i.campo} (${formatarPercentualPontos(i.valor)})`)
        .join(', ');
      return {
        tom: 'negativo',
        selo: 'Percentual fora da faixa',
        rotulo: 'Configuração a corrigir',
        cifra: <Valor valor={null} formato="moeda" tamanho="display" />,
        explicacao: (
          <>
            {lista} {rec.invalidos.length > 1 ? 'estão' : 'está'} fora da faixa de 0% a 100%. O
            laudo não emite veredito de preço nessa condição: com percentual fora da faixa o
            cálculo devolve um mínimo que aponta para prejuízo com aparência de folga. Corrija em
            Despesas, ou a meta em Empresa, e emita o laudo de novo.
          </>
        ),
      };
    }

    default:
      return {
        tom: 'neutro',
        selo: 'Dados insuficientes',
        rotulo: rec.falta === 'volume' ? 'Volume anual' : 'Preço praticado',
        cifra: <Valor valor={null} formato="moeda" tamanho="display" />,
        explicacao:
          rec.falta === 'volume' ? (
            <>
              Falta o volume anual de vendas. Sem ele não existe rateio da despesa fixa, e sem
              rateio não existe lucro por projeto a comparar com a meta.
            </>
          ) : (
            <>
              Falta o valor de venda do projeto. Sem preço praticado não há o que comparar com o
              mínimo para a meta.
            </>
          ),
      };
  }
}
